import * as qs from 'qs';
import { useCallback, useState } from 'react';

import { formatDateForFilename } from '../../../../shared/utils';
import { useAddToast } from '../../../contexts/ToastContext';
import type { IssueExceptionsFilter, IssueExceptionSort } from '../../../../shared/issuesV2';

export type ExportIssueExceptionsParams = {
  filters: IssueExceptionsFilter;
  search?: string;
  sortBy?: IssueExceptionSort;
};

/**
 * Sanitizes a string for use in a filename by replacing special characters with underscores.
 */
function sanitizeForFilename(str: string): string {
  return str.replace(/[^a-zA-Z0-9-]/g, '_');
}

/**
 * Builds a filename for the CSV export based on the filters.
 * Pattern: {CATEGORY}_IGNORE_RULES_{SCOPE}_{identifier}_{timestamp}.csv
 */
function buildExportFilename(filters: IssueExceptionsFilter): string {
  const { category, projectId, releaseGroupId } = filters;
  const timestamp = formatDateForFilename();

  const parts = [category.toUpperCase(), 'IGNORE_RULES'];

  if (projectId) {
    parts.push('PROJECT', sanitizeForFilename(projectId));
  } else if (releaseGroupId) {
    parts.push('RELEASE_GROUP', releaseGroupId.toString());
  }

  parts.push(timestamp);

  return `${parts.join('_')}.csv`;
}

/**
 * Hook that provides a function to export issue exceptions as CSV.
 * Uses fetch + blob to download the file and provides toast feedback on error.
 */
export default function useExportIssueExceptions() {
  const addToast = useAddToast();
  const [isExporting, setIsExporting] = useState(false);

  const exportCsv = useCallback(async (params: ExportIssueExceptionsParams) => {
    const { filters, search, sortBy } = params;

    const queryString = qs.stringify({
      filters,
      search,
      sortBy,
      csv: true,
    });

    setIsExporting(true);

    let didSucceed = false;
    try {
      const response = await fetch(`/api/v2/issues/exceptions?${queryString}`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildExportFilename(filters);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        didSucceed = true;
      }
    } finally {
      setIsExporting(false);
      if (didSucceed) {
        addToast({
          type: 'success',
          content: 'File downloaded!',
        });
      } else {
        addToast({
          type: 'error',
          content: 'Failed to export ignore rules. Please try again.',
        });
      }
    }
  }, [addToast]);

  return { exportCsv, isExporting };
}
